import { useEffect, useState } from "react";
import { useRealtime } from "@/lib/realtime";
import { useAuth } from "@/lib/auth";

const TYPE_LABELS = {
  direct_referral: "Direct Referral",
  binary_matching: "Binary Match",
  monthly_bonus: "Monthly Bonus",
  reward: "Reward",
  income: "Income",
};

function labelFor(txn) {
  if (!txn) return "Credit";
  const desc = (txn.description || "").toLowerCase();
  if (desc.includes("direct referral")) return TYPE_LABELS.direct_referral;
  if (desc.includes("binary")) return TYPE_LABELS.binary_matching;
  if (desc.includes("monthly")) return TYPE_LABELS.monthly_bonus;
  if (desc.includes("reward")) return TYPE_LABELS.reward;
  return "Wallet Credit";
}

/**
 * Listens for `wallet.update` events with a credit txn and animates a floating
 * gold "+₹X · <type>" pill rising from the bottom-right. Stacks up to 4 toasts.
 * Only mounts when a user is logged in (auth.user present).
 */
export default function FloatingCredit() {
  const { user } = useAuth();
  const { subscribe } = useRealtime();
  const [toasts, setToasts] = useState([]); // { id, amount, label }

  useEffect(() => {
    if (!user) return;
    const off = subscribe("wallet.update", (evt) => {
      const txn = evt?.data?.txn;
      if (!txn || txn.direction !== "credit") return;
      const id = `${txn.id || Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      const item = { id, amount: Number(txn.amount) || 0, label: labelFor(txn) };
      setToasts((cur) => [...cur, item].slice(-4));
      // Auto-remove after animation (3.4s)
      setTimeout(() => {
        setToasts((cur) => cur.filter((t) => t.id !== id));
      }, 3400);
    });
    return off;
  }, [user, subscribe]);

  if (!user) return null;

  return (
    <div
      data-testid="floating-credits"
      className="fixed bottom-24 left-6 z-[55] pointer-events-none flex flex-col items-start gap-2"
    >
      {toasts.map((t) => (
        <div
          key={t.id}
          data-testid={`floating-credit-${t.id}`}
          className="px-4 py-2.5 rounded-full bg-gradient-to-r from-gold via-yellow-400 to-gold-deep text-navy shadow-2xl border border-gold-deep/40 inline-flex items-center gap-2 animate-credit-float"
          style={{ filter: "drop-shadow(0 10px 24px rgba(196, 156, 64, 0.4))" }}
        >
          <span className="text-2xl leading-none">✨</span>
          <div className="leading-tight">
            <div className="font-serif text-lg font-semibold">
              +₹{new Intl.NumberFormat("en-IN").format(t.amount)}
            </div>
            <div className="text-[10px] uppercase tracking-widest opacity-80">{t.label}</div>
          </div>
        </div>
      ))}
    </div>
  );
}
